<template>
  <div class="row">
    <div class="col-xl-12">
      <div class="kt-portlet kt-portlet--mobile">
        <div class="kt-portlet__head kt-portlet__head--lg">
          <div class="kt-portlet__head-label">
            <span class="kt-portlet__head-icon">
              <i class="fas fa-search"></i>
            </span>
            <h3 class="kt-portlet__head-title">
              รายละเอียดการใช้งบประมาณ
            </h3>
          </div>
          <div class="kt-portlet__head-toolbar">
            <button type="button" class="btn btn-secondary" @click="goBack">
              <i class="la la-close"></i> ย้อนกลับ
            </button>
          </div>
        </div>
        <div class="kt-portlet__body">
          <!-- Loading -->
          <div v-if="isLoading" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x"></i>
            <p class="mt-2">กำลังโหลดข้อมูล...</p>
          </div>

          <!-- Error -->
          <div v-else-if="errorMessage" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
          </div>

          <!-- Content -->
          <div v-else-if="detailData">
            <!-- Budget Plan Info -->
            <div class="card mb-4">
              <div class="card-header bg-primary text-white">
                <h5 class="mb-0">ข้อมูลงบประมาณ</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">ปีงบประมาณ :</label>
                      <p>{{ detailData.year }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">รหัส CCTR :</label>
                      <p>[{{ detailData.cctrCode }}] {{ detailData.cctrName }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">รหัส IO :</label>
                      <p>[{{ detailData.ioCode }}] {{ detailData.ioName }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">รหัส GL :</label>
                      <p>[{{ detailData.glCode }}] {{ detailData.glName }}</p>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">แผนก :</label>
                      <p>[{{ detailData.orgCode }}] {{ detailData.orgName }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">งบประมาณรวม :</label>
                      <p class="text-success font-weight-bold">{{ formatNumber(budgetTotal) }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">คงเหลืองบประมาณ :</label>
                      <p class="text-info font-weight-bold">{{ formatNumber(budgetRemaining) }}</p>
                    </div>
                  </div>
                  <div class="col-md-3"></div>
                </div>
                <div class="row">
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">งบตั้งต้น :</label>
                      <p class="text-primary font-weight-bold">{{ formatNumber(detailData.initialBudgetAmount) }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">ใช้ไป :</label>
                      <p class="text-danger font-weight-bold">{{ formatNumber(totalUsedAmount) }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">รวมปรับเพิ่มงบประมาณ :</label>
                      <p class="text-success font-weight-bold">{{ formatNumber(totalAddedAmount) }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="font-weight-bold">รวมปรับลดงบประมาณ :</label>
                      <p class="text-warning font-weight-bold">{{ formatNumber(totalReducedAmount) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Job History Table -->
            <div class="card">
              <div class="card-header bg-info text-white">
                <h5 class="mb-0">ประวัติการใช้งบประมาณ</h5>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-bordered table-striped">
                    <thead class="thead-light">
                      <tr>
                        <th class="text-center" width="4%">ลำดับ</th>
                        <th class="text-center" width="10%">เลขที่ใบงาน</th>
                        <th class="text-center" width="10%">ประเภทใบงาน</th>
                        <th class="text-center" width="10%">สถานะใบงาน</th>
                        <th class="text-center" width="10%">วันที่ใบงาน</th>
                        <th class="text-center" width="12%">ใช้ไป</th>
                        <th class="text-center" width="12%">ปรับเพิ่มงบประมาณ</th>
                        <th class="text-center" width="12%">ปรับลดงบประมาณ</th>
                        <th class="text-center" width="14%">คงเหลืองบประมาณ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="job in detailData.jobHistory" :key="job.jobId">
                        <td class="text-center">{{ job.rowNo }}</td>
                        <td class="text-center">
                          <a :href="'/BudgetPlan/Job/View/' + job.jobId" target="_blank">
                            {{ job.jobNumber }}
                          </a>
                        </td>
                        <td class="text-center">{{ job.jobTypeName }}</td>
                        <td class="text-center">
                          <span class="badge"
                            :class="{
                              'badge-secondary': job.status === 0,
                              'badge-warning': job.status === 1 || job.status === 5,
                              'badge-info': job.status === 2,
                              'badge-success': job.status === 3 || job.status === 6,
                              'badge-danger': job.status === 4 || job.status === 7
                            }">
                            {{ job.statusName }}
                          </span>
                        </td>
                        <td class="text-center">{{ job.jobDate }}</td>
                        <td class="text-right">{{ formatNumber(job.usedAmount) }}</td>
                        <td class="text-right">{{ formatNumber(job.addedAmount) }}</td>
                        <td class="text-right">{{ formatNumber(job.reducedAmount) }}</td>
                        <td class="text-right font-weight-bold">{{ formatNumber(job.remainingAmount) }}</td>
                      </tr>
                      <tr v-if="!detailData.jobHistory || detailData.jobHistory.length === 0">
                        <td colspan="9" class="text-center text-muted">
                          ไม่มีประวัติการใช้งบประมาณ
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data: function () {
    return {
      isLoading: false,
      errorMessage: "",
      detailData: null,
    };
  },
  computed: {
    totalUsedAmount: function () {
      if (!this.detailData) return 0;
      return this.detailData.usedAmount || 0;
    },
    totalAddedAmount: function () {
      if (!this.detailData) return 0;
      return this.detailData.addedAmount || 0;
    },
    totalReducedAmount: function () {
      if (!this.detailData) return 0;
      return this.detailData.reducedAmount || 0;
    },
    budgetTotal: function () {
      if (!this.detailData) return 0;
      var A = this.detailData.initialBudgetAmount || 0;
      var B = this.totalAddedAmount - (this.detailData.sendbackAmount || 0); // 18.3: ไม่รวมคืนเงินในงบประมาณรวม
      var D = this.totalReducedAmount;
      return (A + B) - D;
    },
    budgetRemaining: function () {
      if (!this.detailData) return 0;
      var A = this.detailData.initialBudgetAmount || 0;
      var B = this.totalAddedAmount;
      var C = this.totalUsedAmount;
      var D = this.totalReducedAmount;
      return (A + B) - (C + D);
    },
  },
  mounted() {
    this.loadDetail();
  },
  methods: {
    goBack: function () {
      this.$router.go(-1);
    },
    loadDetail: async function () {
      var self = this;
      var budgetPlanId = self.$route.params.id;

      if (!budgetPlanId) {
        self.errorMessage = "ไม่พบรหัสงบประมาณ";
        return;
      }

      self.isLoading = true;
      self.errorMessage = "";

      try {
        var response = await axios.get("/Api/BudgetPlan/" + budgetPlanId + "/Detail", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
        });

        if (response.data && response.data.success) {
          self.detailData = response.data.data;
        } else {
          self.errorMessage = response.data.message || "ไม่สามารถโหลดข้อมูลได้";
        }
      } catch (error) {
        console.error("Error loading detail:", error);
        self.errorMessage = "เกิดข้อผิดพลาดในการโหลดข้อมูล";
      } finally {
        self.isLoading = false;
      }
    },
    formatNumber: function (value) {
      if (value === null || value === undefined) return "0.00";
      return parseFloat(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.card-header {
  padding: 0.75rem 1rem;
}
.card-header h5 {
  margin-bottom: 0;
}
.form-group {
  margin-bottom: 0.5rem;
}
.form-group label {
  margin-bottom: 0.25rem;
  display: block;
}
.form-group p {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// App/Components/BudgetPlan/BudgetPlanDetail.vue